import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardCheck,
  Users,
  UserCheck,
  UserX,
  HelpCircle,
  Clock,
  Download,
  Loader2,
  MessageSquare,
  UtensilsCrossed,
  Calendar,
  AlertCircle
} from 'lucide-react';
import CoupleNavigation from './CoupleNavigation';
import { coupleAuth, wedding } from '../../services/api';

function RSVPDashboard() {
  const navigate = useNavigate();
  const [couple, setCouple] = useState(null);
  const [page, setPage] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPublished, setIsPublished] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [meRes, pageRes, dashRes] = await Promise.all([
        coupleAuth.getMe(),
        wedding.getMyPage(),
        wedding.getRsvpDashboard()
      ]);
      setCouple(meRes.data);
      setPage(pageRes.data);
      setIsPublished(pageRes.data?.is_published || false);
      setDashboard(dashRes.data);
    } catch (err) {
      console.error('RSVP dashboard load error:', err);
      if (err.response?.status === 401) {
        coupleAuth.logout();
        navigate('/couple/login');
        return;
      }
      setError('Failed to load RSVP data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePublishToggle = async () => {
    try {
      const newState = !isPublished;
      await wedding.publishPage(newState);
      setIsPublished(newState);
    } catch (err) {
      console.error('Publish toggle error:', err);
    }
  };

  const handleExportCsv = () => {
    if (!dashboard) return;

    // Build CSV from recent_responses + any available guest data
    const rows = [['Guest Name', 'Status', 'Event', 'Meal Preference', 'Dietary Notes', 'Plus One', 'Message', 'Responded']];

    (dashboard.recent_responses || []).forEach((r) => {
      rows.push([
        r.guest_name || r.name || '',
        r.status || '',
        r.event_name || '',
        r.meal_preference || '',
        r.dietary_notes || '',
        r.plus_one_name || '',
        (r.message || '').replace(/"/g, '""'),
        r.responded_at || r.updated_at || ''
      ]);
    });

    const csv = rows.map((row) => row.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rsvp-responses.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString('en-ZA');
  };

  const getPercent = (count, total) => {
    if (!total) return 0;
    return Math.round((count / total) * 100);
  };

  if (loading) {
    return (
      <CoupleNavigation couple={couple} isPublished={isPublished} onPublishToggle={handlePublishToggle}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-rose-500 animate-spin" />
        </div>
      </CoupleNavigation>
    );
  }

  if (error) {
    return (
      <CoupleNavigation couple={couple} isPublished={isPublished} onPublishToggle={handlePublishToggle}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={loadData}
              className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </CoupleNavigation>
    );
  }

  const totalGuests = dashboard?.total_guests || 0;
  const attending = dashboard?.attending || 0;
  const declined = dashboard?.declined || 0;
  const maybe = dashboard?.maybe || 0;
  const pending = totalGuests - attending - declined - maybe;

  const summaryCards = [
    { label: 'Total Invited', value: totalGuests, pct: null, icon: Users, color: 'bg-blue-50 text-blue-600', iconColor: 'text-blue-500' },
    { label: 'Attending', value: attending, pct: getPercent(attending, totalGuests), icon: UserCheck, color: 'bg-green-50 text-green-600', iconColor: 'text-green-500' },
    { label: 'Declined', value: declined, pct: getPercent(declined, totalGuests), icon: UserX, color: 'bg-red-50 text-red-600', iconColor: 'text-red-500' },
    { label: 'Maybe', value: maybe, pct: getPercent(maybe, totalGuests), icon: HelpCircle, color: 'bg-yellow-50 text-yellow-600', iconColor: 'text-yellow-500' },
    { label: 'Pending', value: pending, pct: getPercent(pending, totalGuests), icon: Clock, color: 'bg-gray-50 text-gray-600', iconColor: 'text-gray-400' }
  ];

  const eventStats = dashboard?.event_stats || [];
  const mealPrefs = dashboard?.meal_preferences || {};
  const recentResponses = dashboard?.recent_responses || [];

  // Meal preferences bar chart data
  const mealEntries = Object.entries(mealPrefs).filter(([, v]) => v > 0);
  const maxMeal = Math.max(...mealEntries.map(([, v]) => v), 1);
  const mealColors = {
    Standard: 'bg-rose-400',
    Vegetarian: 'bg-green-400',
    Vegan: 'bg-emerald-400',
    Halal: 'bg-blue-400',
    Kosher: 'bg-purple-400',
    Other: 'bg-gray-400'
  };

  const statusColors = {
    attending: 'text-green-600',
    yes: 'text-green-600',
    declined: 'text-red-600',
    no: 'text-red-600',
    maybe: 'text-yellow-600',
    pending: 'text-gray-500'
  };

  return (
    <CoupleNavigation couple={couple} isPublished={isPublished} onPublishToggle={handlePublishToggle}>
      <div className="p-4 md:p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">RSVP Dashboard</h1>
            <p className="text-gray-500 text-sm mt-1">Track guest responses at a glance</p>
          </div>
          <button
            onClick={handleExportCsv}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
          {summaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${card.color}`}>
                  <Icon className={`w-4 h-4 ${card.iconColor}`} />
                </div>
                <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-gray-500">{card.label}</p>
                  {card.pct !== null && (
                    <span className="text-xs text-gray-400">({card.pct}%)</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Per-event breakdown */}
          {eventStats.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-rose-500" />
                Event Breakdown
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 pr-4 font-medium text-gray-600">Event</th>
                      <th className="text-center py-2 px-2 font-medium text-green-600">Yes</th>
                      <th className="text-center py-2 px-2 font-medium text-red-600">No</th>
                      <th className="text-center py-2 px-2 font-medium text-gray-500">Pending</th>
                      <th className="text-center py-2 pl-2 font-medium text-purple-600">+1s</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventStats.map((event, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        <td className="py-2.5 pr-4 font-medium text-gray-900">{event.event_name || event.name}</td>
                        <td className="py-2.5 px-2 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-green-50 text-green-700 text-xs font-semibold">
                            {event.attending || 0}
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-red-50 text-red-700 text-xs font-semibold">
                            {event.declined || 0}
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-gray-50 text-gray-600 text-xs font-semibold">
                            {event.pending || 0}
                          </span>
                        </td>
                        <td className="py-2.5 pl-2 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-purple-50 text-purple-700 text-xs font-semibold">
                            {event.plus_ones || 0}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Meal preferences */}
          {mealEntries.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <UtensilsCrossed className="w-5 h-5 text-rose-500" />
                Meal Preferences
              </h2>
              <div className="space-y-3">
                {mealEntries.map(([meal, count]) => (
                  <div key={meal}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-700">{meal}</span>
                      <span className="text-sm font-semibold text-gray-900">{count}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all ${mealColors[meal] || 'bg-gray-400'}`}
                        style={{ width: `${(count / maxMeal) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Recent responses */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-rose-500" />
            Recent Responses
          </h2>
          {recentResponses.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">No responses yet. Share your RSVP links to get started.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentResponses.map((resp, idx) => {
                const status = resp.status || 'pending';
                const statusLabel = status === 'yes' || status === 'attending' ? 'Attending'
                  : status === 'no' || status === 'declined' ? 'Declined'
                  : status === 'maybe' ? 'Maybe' : 'Pending';
                return (
                  <div key={idx} className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
                    <div className="w-9 h-9 bg-rose-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-rose-600 font-semibold text-sm">
                        {(resp.guest_name || resp.name || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900 text-sm">
                          {resp.guest_name || resp.name}
                        </span>
                        <span className={`text-xs font-semibold ${statusColors[status] || 'text-gray-500'}`}>
                          {statusLabel}
                        </span>
                        {resp.event_name && (
                          <span className="text-xs text-gray-400">for {resp.event_name}</span>
                        )}
                      </div>
                      {resp.message && (
                        <p className="text-sm text-gray-500 mt-1 flex items-start gap-1.5">
                          <MessageSquare className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
                          <span className="italic">"{resp.message}"</span>
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                      {getTimeAgo(resp.responded_at || resp.updated_at)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </CoupleNavigation>
  );
}

export default RSVPDashboard;
